import { createSlice, createSelector /* , current */ } from '@reduxjs/toolkit';
import { get, set, isString, isObject } from 'lodash';
import { mergeProductOverride } from '../utils/configUtils';
import { primary } from '../defaults';

const initialState = {
  isWaitingForConf: true,
};

// Per-instance non-serializable data storage.
// fields, interfaces, and callbacks cannot live in Redux because they contain
// non-serializable values (yup validators, JSX, functions). Each widget instance
// gets its own entry keyed by instanceId (set via preloadedState in store factory).
const instanceDataMap = new Map();

function getInstanceData(instanceId) {
  if (!instanceDataMap.has(instanceId)) {
    instanceDataMap.set(instanceId, {
      fields: {},
      interfaces: {},
      callbacks: {},
      // Processed product-scoped appConfig override. null when no product is
      // selected or the selected product has no override. Kept outside Redux
      // because override.fields may carry non-serializable state once validators
      // are attached (same rationale as `fields`, `interfaces`, `callbacks`).
      productAppConfigOverride: null,
      cache: {
        prevAppConfig: null,
        prevFields: null,
        prevInterfaces: null,
        prevCallbacks: null,
        prevProductAppConfigOverride: null,
        cachedResult: null,
      },
    });
  }
  return instanceDataMap.get(instanceId);
}

const tryParseJSON = (value) => {
  try {
    return { value: JSON.parse(value), valid: true };
  } catch (e) {
    return { value: value, valid: false };
  }
};

const isPDataValid = (pData) => {
  return isObject(pData) && pData?.personId && pData?.platform;
};

export const appStateSlice = createSlice({
  name: 'appState',
  initialState,
  reducers: {
    reset: {
      reducer(state) {
        const instanceId = state.instanceId;
        // Clean up per-instance non-serializable state
        instanceDataMap.delete(instanceId);
        // Return initial Redux state, preserving instanceId
        return { ...initialState, instanceId };
      },
    },
    setAppConfig: {
      reducer(state, action) {
        // setting appConfig to undefined is an invalid state change
        if (!action.payload) {
          return;
        }

        // normalize pData first
        let pData = action.payload.pData;
        if (pData) {
          if (isString(pData)) {
            pData = tryParseJSON(pData);

            if (pData?.valid) {
              pData = pData.value;
            }
          }

          pData.personId =
            pData.personId || pData.address || pData.partnerId || pData.id;

          if (isPDataValid(pData)) {
            action.payload.pData = pData;
          } else {
            delete action.payload.pData;
            console.warn(
              'malformed pData parameter detected. Tried to parse as JSON but failed. Ignoring param for now.',
            );
          }
        }

        // fields, interfaces, and callbacks are non-serializable — store per-instance
        const data = getInstanceData(state.instanceId);
        data.fields = action.payload.fields;
        delete action.payload.fields;

        data.interfaces = action.payload.interfaces;
        delete action.payload.interfaces;

        data.callbacks = action.payload.callbacks;
        delete action.payload.callbacks;

        state.appConfig = action.payload;
        // console.log(JSON.stringify(current(state), undefined, 2));
      },
    },
    mutateAppConfig: {
      reducer(state, action) {
        set(
          state.appConfig,
          action.payload.pathToProp,
          action.payload.newValue,
        );
      },
    },
    setFcConfig: {
      reducer(state, action) {
        state.fcConfig = action.payload;
        // console.log(JSON.stringify(current(state), undefined, 2));
      },
    },
    setMuiTheme: {
      reducer(state, action) {
        const theme = action.payload;
        if (theme?.typography?.pxToRem) {
          const data = getInstanceData(state.instanceId);
          data.pxToRem = theme.typography.pxToRem;
          delete theme.typography.pxToRem;
        }
        state.muiTheme = theme;
      },
    },
    setIdentifiedCustomer: {
      reducer(state, action) {
        state.customerData = action.payload;
      },
    },
    setIsIdentifyingCustomer: {
      reducer(state, action) {
        state.isIdentifyingCustomer = action.payload;
      },
    },
    setCustomerIdentificationFailed: {
      reducer(state, action) {
        state.customerIdentificationFailed = action.payload;
      },
    },
    setIsWaitingForConf: {
      reducer(state, action) {
        state.isWaitingForConf = action.payload;
      },
    },
    selectPublicData: {
      reducer(state, action) {
        state.selectedPublicData = action.payload;
        state.selectedResource = action.payload?.resource;
        // console.log(JSON.stringify(current(state), undefined, 2));
      },
    },
    selectProduct: {
      reducer(state, action) {
        if (typeof action?.payload?.price === 'string') {
          console.error('Selected mutated product instance.');
        }
        state.selectedProduct = action.payload;
        // console.log(JSON.stringify(current(state), undefined, 2));
      },
    },
    selectResource: {
      reducer(state, action) {
        state.selectedResource = action.payload;
        const associatedPublicData =
          state.resToPublicDataMap[action.payload?.resource?.uuid];
        state.selectedPublicData = associatedPublicData;
        // console.log(JSON.stringify(current(state), undefined, 2));
      },
    },
    setResToPublicDataMap: {
      reducer(state, action) {
        state.resToPublicDataMap = action.payload;
        // console.log(JSON.stringify(current(state), undefined, 2));
      },
    },
    setProductAppConfigOverride: {
      reducer(state, action) {
        const data = getInstanceData(state.instanceId);
        data.productAppConfigOverride = action.payload ?? null;
        state.productAppConfigOverrideRev =
          (state.productAppConfigOverrideRev ?? 0) + 1;
      },
    },
  },
});

export const {
  setAppConfig,
  mutateAppConfig,
  setFcConfig,
  setMuiTheme,
  selectProduct,
  selectResource,
  selectPublicData,
  setResToPublicDataMap,
  setIdentifiedCustomer,
  setIsIdentifyingCustomer,
  setCustomerIdentificationFailed,
  setIsWaitingForConf,
  setProductAppConfigOverride,
  reset,
} = appStateSlice.actions;

export const selectMuiTheme = (state) => {
  const theme = state.appState.muiTheme;
  if (!theme) return theme;
  const data = getInstanceData(state.appState.instanceId);
  if (data.pxToRem && theme.typography) {
    return { ...theme, typography: { ...theme.typography, pxToRem: data.pxToRem } };
  }
  return theme;
};

export const selectMuiThemeProp = (state, pathToProp) => {
  return get(state.appState.muiTheme, pathToProp);
};

export const selectFcConfig = (state) => {
  return state.appState.fcConfig;
};

export const selectFcConfigProp = (state, pathToProp) => {
  return get(state.appState.fcConfig, pathToProp);
};

/**
 * Selects the whole app config with manual memoization.
 *
 * fields must be writable (several reasons: React/react-hook-form need to add properties/functions)
 * also: yup validation functions aren't serializable so we transparently move all that into a separate object outside of the store
 *
 * The same reasons apply to the interfaces. Since they are JSX react needs to have full access.
 *
 * Note: fields, interfaces, and callbacks are reassigned (not mutated) in setAppConfig,
 * so reference equality checks are sufficient for cache invalidation.
 *
 * @param {object} state - Redux state
 * @returns {object} App config with non-serializable data merged in
 */
export const selectAppConfig = (state) => {
  const appConfig = state.appState.appConfig;
  const data = getInstanceData(state.appState.instanceId);
  const { cache } = data;

  // Return cached result if nothing has changed
  if (
    cache.cachedResult &&
    Object.is(cache.prevAppConfig, appConfig) &&
    cache.prevFields === data.fields &&
    cache.prevInterfaces === data.interfaces &&
    cache.prevCallbacks === data.callbacks &&
    cache.prevProductAppConfigOverride === data.productAppConfigOverride
  ) {
    return cache.cachedResult;
  }

  cache.prevAppConfig = appConfig;
  cache.prevFields = data.fields;
  cache.prevInterfaces = data.interfaces;
  cache.prevCallbacks = data.callbacks;
  cache.prevProductAppConfigOverride = data.productAppConfigOverride;

  const baseline = {
    ...appConfig,
    fields: { ...data.fields },
    interfaces: { ...data.interfaces },
    callbacks: { ...data.callbacks },
  };

  cache.cachedResult = mergeProductOverride(
    baseline,
    data.productAppConfigOverride,
    primary.appConfig,
  );
  return cache.cachedResult;
};

export const selectAppConfigProp = (state, pathToProp) => {
  return get(selectAppConfig(state), pathToProp);
};

export const selectSelectedResource = (state) => {
  return state.appState.selectedResource;
};

export const selectSelectedProduct = createSelector(
  [(state) => state.appState.selectedProduct, selectSelectedResource],
  (selectedProduct, selectedResource) => {
    if (!selectedProduct || !selectedResource) {
      return selectedProduct;
    }

    const productWithPrice = { ...selectedProduct };

    if (selectedProduct.price && selectedProduct.price[selectedResource.uuid]) {
      productWithPrice.price = selectedProduct.price[selectedResource.uuid];
    } else {
      delete productWithPrice.price;
    }

    return productWithPrice;
  },
);

export const selectCustomerData = (state) => {
  return state.appState.customerData;
};

export const selectIsIdentifyingCustomer = (state) => {
  return state.appState.isIdentifyingCustomer;
};

export const selectCustomerIdentificationFailed = (state) => {
  return state.appState.customerIdentificationFailed;
};

export const selectIsWaitingForConf = (state) => {
  return state.appState.isWaitingForConf;
};

export const selectSelectedPublicData = (state) => {
  return state.appState.selectedPublicData;
};

export const selectIsThemingAllowed = (state) => {
  return state.appState.selectedPublicData?.provider?.isThemingAllowed;
};

export const selectAreCustomFieldsAllowed = (state) => {
  return state.appState.selectedPublicData?.provider?.areCustomFieldsAllowed;
};

export const selectIsLocalisationAllowed = (state) => {
  return state.appState.selectedPublicData?.provider?.isLocalisationAllowed;
};

export const selectPublicDataOfRes = (state, uuid) => {
  return state.appState.resToPublicDataMap[uuid];
};

export const selectResToPublicDataMap = (state) => {
  return state.appState.resToPublicDataMap;
};

export const selectResInPublicData = () =>
  createSelector([selectResToPublicDataMap], (publicDataMap) => {
    return Object.values(publicDataMap).map(
      (publicData) => publicData?.resource,
    );
  });

export default appStateSlice.reducer;
